import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Play } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleAuthButton } from '../components/GoogleAuthButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Welcome back!');
      navigate('/browse');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 group">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <Play className="w-7 h-7 text-black fill-black" />
            </div>
            <span className="font-heading text-3xl tracking-wider text-white">
              AFROSPORTS
            </span>
          </Link>
        </div>

        {/* Brand Tagline */}
        <div className="text-center mb-6">
          <p className="text-white font-semibold text-lg leading-snug">The Home of African Sport.</p>
          <p className="text-muted-foreground text-sm mt-1">Live matches. Exclusive competitions. Original programming — all in one place.</p>
        </div>

        {/* Form */}
        <div className="glass-effect rounded-2xl p-8" data-testid="login-form">
          <h1 className="font-heading text-3xl text-white mb-2 text-center">WELCOME BACK</h1>
          <p className="text-muted-foreground text-center mb-8">Login to continue streaming</p>

          {/* Google Auth Button */}
          <GoogleAuthButton className="w-full mb-6" />

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                className="mt-2 bg-black/50 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-1 focus:ring-primary h-12"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                className="mt-2 bg-black/50 border-white/20 text-white placeholder:text-white/40 focus:border-primary focus:ring-1 focus:ring-primary h-12"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-black hover:bg-primary/90 font-bold text-lg h-12 rounded-full uppercase tracking-wider"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-primary hover:text-primary/80 text-sm">
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:text-primary/90 font-bold" data-testid="signup-link">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}